import React, { memo, useRef, useState, useEffect } from 'react';

import LivevideoIndicatorComponent from "../LivevideoIndicatorComponent/index";
import LiveStatusComponent from "../LiveStatusComponent/index";
import ZoomComponent from "../../reusable/ZoomComponent/index";
import useRunLiveVideo from "../../../hooks/useRunLiveVideo";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/layouts/types";
import useObjectFitElement from "../../../hooks/useObjectFitElement";
import PTZIconComponent from "../../ui/PTZIconComponent";
import { CamerasState } from "../../../store/cameras/types";
import { setPTZMode } from "../../../store/cameras/actions";
import TriggersIconUIComponent from "../../ui/TriggersIconUIComponent";
import './style.scss';
import TriggersComponent from "../../reusable/TriggersComponent";
import { IconComponent } from "../../reusable/simpleComponents";
import PresetsComponent from "../PresetsComponent";
import useHeartBeatenPane from "../../../hooks/useHeartBeatenPane";
import { RouterState } from 'connected-react-router';
import { Routes } from '../../../types';

interface Props {
  streamName: string,
  pane: number,
  setVideoIndicator: (status: string) => void
}

interface ZoomMemoInterface {
  pane: number,
  forwardRef: React.RefObject<HTMLVideoElement> | any
  enable: boolean,
  digitalPTZ?: boolean,
  PTZCapibility?: any,
  status: string
}

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    // extends React's HTMLAttributes
    pluginspage?: string;
    autoplay?: string;
    loop?: string;
    target?: string;
    classid?: string;
    codebase?: string;
  }
}


const ZoomVideoComponent = memo(({forwardRef, pane, enable, digitalPTZ, PTZCapibility, status}: ZoomMemoInterface) => {
  useObjectFitElement(forwardRef);
  useHeartBeatenPane(pane);

  return (
    <ZoomComponent enable={enable}
                   digitalPTZ={digitalPTZ}
                   pane={pane}
                   PTZCapibility={PTZCapibility}
    >
      <video ref={forwardRef}
             className='video'
             id={`video-${pane}`}
      />

    </ZoomComponent>
  )
});


const LiveVideoComponent: React.FC<Props> = ({streamName, pane, setVideoIndicator}) => {
  const ref = useRef(null);
  const paneRef = useRef(null);
  const [triggersOpened, setTriggersOpened] = useState(false);
  const [presetsOpened, setPresetsOpened] = useState(false);
  const {openedCameraList} = useSelector<ApplicationState, CamerasState>(state => state.cameras);
  const {checkedPane} = useSelector<ApplicationState, LayoutState>(state => state.layouts);
  const {status, speed, lost} = useRunLiveVideo(ref, streamName, `video-${pane}`);
  const {ptzModes, ptzCapibilities, currentCameras, presets} = useSelector<ApplicationState, CamerasState>(state => state.cameras);
  const {location} = useSelector<ApplicationState, RouterState>(state => state.router);
  const dispatch = useDispatch();
  const PTZCapibility = ptzCapibilities[pane];

  const digitalPTZ = ptzModes[pane];

  const triggerPTZMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const mode = !!ptzModes[pane];
    dispatch(setPTZMode(pane, !mode))
  };

  const doTriggerPTZ = (triggerId: string) => {
    dispatch({type: 'ptzTrigger', pane, triggerId});
    setTriggersOpened(false)
  };

  const doPresetAction = (presetId: string) => {
    dispatch({type: "gotoPreset", pane, presetId});
    setPresetsOpened(false)
  };

  useEffect(() => {
    if (PTZCapibility?.has_ptz) {
      const mode = !!ptzModes[pane];
      dispatch(setPTZMode(pane, !mode))
    }
  }, [PTZCapibility])

  useEffect(() => {
    setTriggersOpened(false);
    setPresetsOpened(false);
  }, [checkedPane, openedCameraList]);

  useEffect(() => {
    setVideoIndicator(status);
  }, [status]);

  return (
    <div className={`w-100 pos-rel h-100`} ref={paneRef}>
      <LivevideoIndicatorComponent status={status}/>
      <LiveStatusComponent pane={pane}
                           speed={speed}
                           lost={lost}
                           status={status}
                           forwardRef={paneRef}
      />
      <ZoomVideoComponent forwardRef={ref}
                          pane={pane}
                          enable={checkedPane === pane}
                          digitalPTZ={digitalPTZ}
                          PTZCapibility={PTZCapibility}
                          status={status}
      />
      {
        (PTZCapibility?.has_ptz && checkedPane === pane) && <div className="ptzcamera-label">PTZ</div>
      }
      <div className={'pos-abs pane-controls'}>
        {
          (PTZCapibility?.has_ptz && checkedPane === pane) && <div className="ptz-wrapper">
            <div className="ptzicon-container">
              <PTZIconComponent
                classValue={digitalPTZ ? 'ptz-icon' : 'optical-icon'}
                onClick={triggerPTZMode}
                title={digitalPTZ ? 'Switch to Digital PTZ' : 'Switch to Optical PTZ'}
              />
            </div>
          </div>
        }
        {
          (checkedPane === pane
            && typeof (currentCameras[pane]) !== 'undefined'
            && currentCameras?.[pane].triggers
            && currentCameras?.[pane].triggers.length > 0) &&
          <div className="triggers-container">
            <TriggersIconUIComponent
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setTriggersOpened(!triggersOpened)
              }}
            />
          </div>
        }
        {
          (triggersOpened && location.pathname !== Routes.alerts) && <TriggersComponent
            triggers={typeof currentCameras[pane] !== 'undefined' && currentCameras[pane].triggers}
            onClick={doTriggerPTZ}
            onClose={() => setTriggersOpened(false)}
            style={{left: 'auto'}}
            title="Camera Triggers"
          />
        }
        {
          (checkedPane === pane && presets[pane] && presets[pane].length > 0) && <div className='presets-container'>
            <IconComponent classValue='presets-icon icon'
                           handler={() => setPresetsOpened(!presetsOpened)}
                           title="Select Preset"
            />
          </div>
        }
        {
          presetsOpened && <PresetsComponent
            presets={presets[pane]}
            onClick={doPresetAction}
            onClose={() => setPresetsOpened(false)}
            pane={pane}
            title="Select Preset"
          />
        }
      </div>
    </div>
  )
};

export default LiveVideoComponent